import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useOutletContext } from 'react-router-dom';

import {
    getCatalogItems,
    getCatalogItemsAreLoading,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import {
    getSelectCategoryTitle,
    getSelectSubCategoryName,
} from 'entities/Category/model/selectors/categorySelectors';
import { MoveToInventoryCatalogItemButton } from 'features/moveToInventoryCatalogItem';
import { RemoveCatalogItem } from 'features/removeCatalogItem';
import { getRouteGlobalCatalog } from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogItemsList } from './PropertyCatalogItemsList';

interface PropertyCatalogItemsOutletProps {
    hasCatalogItems: boolean;
    showBulkPanel: boolean;
    moveTo: PropertyCatalogItemsMoveTo;
}

export const PropertyCatalogItemsOutlet = () => {
    const { hasCatalogItems, showBulkPanel, moveTo } =
        useOutletContext<PropertyCatalogItemsOutletProps>();

    const selectedItems = useSelector(getSelectedCatalogItems);
    const location = useLocation();
    const catalogItems = useSelector(getCatalogItems);
    const loading = useSelector(getCatalogItemsAreLoading);
    const categoryTitle = useSelector(getSelectCategoryTitle);
    const subCategoryName = useSelector(getSelectSubCategoryName);
    const isGlobalCatalogRoute = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );
    const isOrderGlobalCatalog = useIsOrdersAddItemsMarketplace();
    const isGlobalCatalogMode = Boolean(
        isGlobalCatalogRoute || isOrderGlobalCatalog,
    );
    const currentPageIsOrders = useMatch('/orders' + '/*');

    const categoryHeaderText = useMemo(() => {
        if (!categoryTitle) {
            return null;
        }

        if (isGlobalCatalogMode && subCategoryName) {
            return `${categoryTitle} / ${subCategoryName}`;
        }

        return categoryTitle;
    }, [categoryTitle, isGlobalCatalogMode, subCategoryName]);

    const isSingleItem = Object.keys(selectedItems || {})?.length === 1;
    const isOrderEditing = location.state?.selectedOrderId;

    const scrollableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo(0, 0);
        }
    }, [catalogItems]);

    return (
        <Box
            flex="1 1"
            p="0 16px 16px"
            display="flex"
            flexDirection="column"
            sx={{ overflowY: 'scroll' }}
            ref={scrollableRef}
        >
            {categoryHeaderText ? (
                <Typography
                    data-testclass="catalog-items-category-header"
                    typography="openSans.subtitle1Medium"
                    color="text.primary"
                    paddingTop="16px"
                    paddingBottom="16px"
                >
                    {categoryHeaderText}
                </Typography>
            ) : null}
            {hasCatalogItems && !isGlobalCatalogMode && showBulkPanel ? (
                <Box
                    display="flex"
                    gap="24px"
                    justifyContent="flex-end"
                    p="16px 0"
                    bgcolor="background.paper"
                    zIndex="drawer"
                    minHeight="55px"
                >
                    {showBulkPanel ? (
                        <>
                            <RemoveCatalogItem isSingleItem={isSingleItem} />
                            <MoveToInventoryCatalogItemButton
                                isSingleItem={isSingleItem}
                            />
                        </>
                    ) : null}
                </Box>
            ) : null}
            <PropertyCatalogItemsList
                items={catalogItems}
                loading={loading}
                moveTo={moveTo}
                quickEdit={isOrderEditing}
                isOrderPage={!!currentPageIsOrders}
            />
        </Box>
    );
};
